import { useState, useEffect } from 'react';

const DISCOUNT_DURATION_MS =  2 * 24 * 60 * 60 * 1000 ; // 2 days
const LOCAL_STORAGE_KEY = 'firstVisitTime';

/**
 * A hook to manage a limited-time discount for new visitors.
 * It uses localStorage to track the first visit.
 *
 * @returns {object} An object containing:
 * - {boolean} isDiscountActive: Whether the discount period is currently active.
 * - {Date | null} discountEndDate: The date when the discount expires.
 * - {Function} getRemainingTime: A function that returns the remaining time as an object { days, hours, minutes, seconds }.
 */
export const useDiscount = () => {
  const [isDiscountActive, setIsDiscountActive] = useState(false);
  const [discountEndDate, setDiscountEndDate] = useState(null);

  useEffect(() => {
    try {
      const firstVisitTimeStr = localStorage.getItem(LOCAL_STORAGE_KEY);
      let firstVisitTime;

      if (firstVisitTimeStr) {
        // Use existing visit time
        firstVisitTime = new Date(parseInt(firstVisitTimeStr, 10));
      } else {
        // Set first visit time for new user
        firstVisitTime = new Date();
        localStorage.setItem(LOCAL_STORAGE_KEY, firstVisitTime.getTime().toString());
      }

      const endDate = new Date(firstVisitTime.getTime() + DISCOUNT_DURATION_MS);
      setDiscountEndDate(endDate);

      // Check if discount is currently active
      const now = new Date();
      if (now < endDate) {
        setIsDiscountActive(true);
      } else {
        setIsDiscountActive(false);
      }
    } catch (error) {
      console.error("Could not access local storage for discount:", error);
      // Disable discount if local storage is unavailable
      setIsDiscountActive(false);
      setDiscountEndDate(null);
    }
  }, []);

  // Add a timer to continuously check if discount has expired
  useEffect(() => {
    if (!discountEndDate || !isDiscountActive) {
      return;
    }

    const checkDiscountExpiry = () => {
      const now = new Date();
      if (now >= discountEndDate) {
        setIsDiscountActive(false);
      }
    };

    // Check immediately
    checkDiscountExpiry();

    // Set up interval to check every second
    const interval = setInterval(checkDiscountExpiry, 1000);

    return () => clearInterval(interval);
  }, [discountEndDate, isDiscountActive]);

  const getRemainingTime = () => {
    if (!discountEndDate || !isDiscountActive) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const total = discountEndDate.getTime() - new Date().getTime();
    
    // If time has expired, return zeros
    if (total <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));

    return {
      days,
      hours,
      minutes,
      seconds,
    };
  };

  return { isDiscountActive, discountEndDate, getRemainingTime };
}; 